import { memo, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Button,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Card,
  CardHeader,
  CardBody,
  Select,
  SelectItem,
  Spinner,
  Chip,
  Tabs,
  Tab,
  SortDescriptor,
} from '@nextui-org/react';
import ExcelExportButton from '../ui/ExcelExportButton';
import {
  ClientRanking,
  ClientRankingsExporters,
  Settlement,
} from 'common/src/utils/types';
import { getSettlementsVsExporters } from 'common/src/api/settlements';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { calculateClientRankings } from '../../services/settlements_vs';
import InformationTooltip from '../../../../common/src/components/ui/InformationTooltip';
import { formatMoneyUSD, formatNumber } from '../../utils/functions';
import { supabase } from 'common/src/supabase';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { UNIFIED_TABLE_CLASSNAMES, SUMMARY_ROW_CLASS } from '../../utils/table-styles';
import { exportTableToExcel } from '../../utils/export-to-excel';

interface Props {
  settlements: Settlement[];
  originalSettlements: Settlement[];
  sinisterFilter: 'all' | 'sinister' | 'non_sinister';
  speciesId?: number;
  season?: string;
  includeRevenues?: boolean;
  // Sidebar-side narrowing, applied server-side to the user's own settlements
  // before rankings are computed. Industry benchmark stays broad.
  receiversFilter?: string[];
  marketsFilter?: string[];
  countriesFilter?: string[];
  costMethodology?: string;
}

// Column constants are now created inside the component to use translations

// Orden personalizado para calibres (de mayor a menor)
const CALIBER_ORDER = ['5J', '4J', '3J', '2J', 'J', 'XL', 'L'];

const getCaliberSortIndex = (caliber: string): number => {
  const index = CALIBER_ORDER.indexOf(caliber);
  return index === -1 ? 999 : index; // Si no está en la lista, va al final
};

// Pill-styled percentage delta with an optional secondary "comparable" badge
// underneath. Replaces the previous colored-text approach for easier scanning.
const DifferenceCell = ({
  value,
  comparablePercentage,
}: {
  value: number | null;
  comparablePercentage?: number;
  comparableText?: string;
}) => {
  if (value === null)
    return <span className='text-default-300 text-sm'>N/A</span>;

  const isPositive = value > 0;
  const isZero = value === 0;
  const pillClass = isZero
    ? 'bg-default-100 text-default-600 dark:bg-default-50 dark:text-default-400'
    : isPositive
      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';

  return (
    <div className='flex flex-col items-start gap-0.5'>
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums ${pillClass}`}
      >
        {isPositive ? '+' : ''}
        {formatNumber(value, { decimals: 1 })}%
      </span>
      {comparablePercentage !== undefined && (
        <span className='text-[10px] text-default-400 ml-1'>
          {comparablePercentage.toFixed(0)}% comp.
        </span>
      )}
    </div>
  );
};

// Kilos + share-of-total, styled to match FobCell (primary number + muted
// percentage secondary line, right-aligned).
const VolumeCell = ({
  kilos,
  grandTotalKilos,
}: {
  kilos: number;
  grandTotalKilos: number;
}) => {
  const pct = grandTotalKilos > 0 ? (kilos / grandTotalKilos) * 100 : 0;
  return (
    <div className='flex flex-col items-end'>
      <span className='font-semibold tabular-nums text-sm'>
        {formatNumber(kilos, { decimals: 0 })}
      </span>
      <span className='text-[10px] text-default-400 tabular-nums'>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
};

// FOB with optional share-of-total displayed as a muted secondary line.
const FobCell = ({
  fob,
  grandTotalFob,
}: {
  fob: number;
  grandTotalFob: number;
}) => {
  const pct = grandTotalFob > 0 ? (fob / grandTotalFob) * 100 : 0;
  return (
    <div className='flex flex-col items-end'>
      <span className='font-semibold tabular-nums text-sm'>
        {formatMoneyUSD(fob, { decimals: 0 })}
      </span>
      <span className='text-[10px] text-default-400 tabular-nums'>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
};

const RankingClientsTable = ({
  settlements,
  sinisterFilter,
  // Retained for API compatibility with existing call sites; no longer used
  // since the "clients filtered" chip moved up to a section-level disclaimer.
  originalSettlements: _originalSettlements,
  speciesId,
  season,
  includeRevenues,
  receiversFilter,
  marketsFilter,
  countriesFilter,
  costMethodology,
}: Props) => {
  const { t } = useTranslation('settlements');
  const { userProfile, session } = useUserStore();
  const [rankings, setRankings] = useState<ClientRanking[]>([]);
  const [speciesCalibers, setSpeciesCalibers] = useState<string[] | null>(null);

  // Define columns with translations. Volume and FOB columns give scale
  // context — crucial for reading the percentage-difference columns.
  const COLUMNS_CLIENT = [
    { name: t('NOMBRE'), uid: 'client' },
    { name: t('VOLUMEN (KG)'), uid: 'volume' },
    { name: t('FOB'), uid: 'fob' },
    { name: t('VTA VS MERCADO'), uid: 'difference' },
    { name: t('CIF VS INDUSTRIA'), uid: 'difference_exporters' },
    { name: t('CIF VS EXPORTADORA'), uid: 'difference_clients' },
  ] as const;

  const COLUMNS_VARIETY_CALIBER = [
    { name: t('NOMBRE'), uid: 'client' },
    { name: t('VOLUMEN (KG)'), uid: 'volume' },
    { name: t('FOB'), uid: 'fob' },
    { name: t('VTA VS MERCADO'), uid: 'difference' },
    { name: t('CIF VS INDUSTRIA'), uid: 'difference_exporters' },
  ] as const;

  // Estados para los 3 tabs
  const [rankingsClient, setRankingsClient] = useState<
    ClientRankingsExporters[]
  >([]);
  const [rankingsVariety, setRankingsVariety] = useState<
    ClientRankingsExporters[]
  >([]);
  const [rankingsCaliber, setRankingsCaliber] = useState<
    ClientRankingsExporters[]
  >([]);

  const [loading, setLoading] = useState(true);
  // Track whether we've completed at least one fetch. After that, subsequent
  // refetches (from filter changes) keep the previous table visible with a
  // small "Actualizando..." badge instead of replacing it with a spinner.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('client');

  const [weightingType, setWeightingType] = useState<'volume' | 'fob'>(
    'fob'
  );
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({});

  // Pagination for each tab's table. Resets to 10 whenever the user switches
  // tabs so each tab starts fresh with its top results.
  const [visibleCount, setVisibleCount] = useState(10);
  useEffect(() => {
    setVisibleCount(10);
  }, [activeTab]);

  // Any sidebar filter narrowing the dataset? Used to flag the CIF VS
  // EXPORTADORA column, whose value is computed against the user's other
  // clients — so filters directly shrink the comparison set.
  const hasActiveFilters =
    sinisterFilter !== 'all' ||
    receiversFilter !== undefined ||
    marketsFilter !== undefined ||
    countriesFilter !== undefined ||
    (costMethodology !== undefined && costMethodology !== 'all');

  // Memoize fetch function - 1 sola llamada que retorna los 3 groupings
  const fetchData = useCallback(
    async (exporterId: number) => {
      try {
        setLoading(true);

        const result = await getSettlementsVsExporters(
          exporterId,
          session,
          sinisterFilter,
          speciesId,
          season,
          includeRevenues,
          {
            receivers: receiversFilter,
            markets: marketsFilter,
            countries: countriesFilter,
            cost_methodology: costMethodology,
          }
        );

        if (result) {
          // Por Cliente: default to biggest clients first. The visible columns
          // are difference percentages which aren't meaningful as a default
          // sort, so we pre-sort by totalFob descending here and let the
          // user re-sort via column headers.
          const sortedClient = [...(result.rankingsClient || [])].sort(
            (a, b) => b.totalFob - a.totalFob
          );
          setRankingsClient(sortedClient);
          // Ordenar variedades por volumen (mayor a menor)
          const sortedVariety = (result.rankingsVariety || []).sort((a, b) =>
            b.totalKilos - a.totalKilos
          );
          setRankingsVariety(sortedVariety);

          // Filtrar y ordenar calibres de mayor a menor
          // Obtener los calibers permitidos (si existen)
          const allowedCalibers = speciesCalibers && speciesCalibers.length > 0
            ? new Set(speciesCalibers.map(c => c.toLowerCase()))
            : null;

          let filteredCaliber = result.rankingsCaliber || [];

          // Filtrar por calibres permitidos si existen (case-insensitive)
          if (allowedCalibers) {
            filteredCaliber = filteredCaliber.filter((ranking) =>
              allowedCalibers.has(ranking.client.toLowerCase())
            );
          }

          // Ordenar calibres de mayor a menor
          const sortedCaliber = filteredCaliber.sort((a, b) =>
            getCaliberSortIndex(a.client) - getCaliberSortIndex(b.client)
          );
          setRankingsCaliber(sortedCaliber);
        }
      } catch (error) {
        console.error('Error fetching data', error);
      } finally {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    },
    [
      sinisterFilter,
      speciesId,
      season,
      speciesCalibers,
      includeRevenues,
      receiversFilter,
      marketsFilter,
      countriesFilter,
      costMethodology,
    ]
  );

  useEffect(() => {
    if (userProfile?.exporter_id) {
      fetchData(userProfile.exporter_id);
    }
  }, [userProfile?.exporter_id, fetchData]);

  useEffect(() => {
    const newRankings = calculateClientRankings(settlements);
    setRankings(newRankings);
  }, [settlements]);

  // Obtener calibers de la especie seleccionada directamente de la BD
  useEffect(() => {
    const fetchSpeciesCalibers = async () => {
      if (!speciesId) {
        setSpeciesCalibers(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('species')
          .select('calibers')
          .eq('id', speciesId)
          .single();

        if (error) throw error;

        setSpeciesCalibers(data?.calibers || null);
      } catch (error) {
        console.error('Error fetching species calibers:', error);
        setSpeciesCalibers(null);
      }
    };

    fetchSpeciesCalibers();
  }, [speciesId]);

  // Calculate rankings grouped by variety
  // Grand totals derived straight from the filtered settlements prop so the
  // totals row stays identical across the three tabs. Per-tab memos
  // (rankingsVarietyGrouped / rankingsCaliberGrouped) legitimately drop details
  // that don't match their grouping rule (e.g. caliber not in species list),
  // so their summed rows may not equal this figure — which is fine; the totals
  // row is "total filtered FOB", not "sum of visible rows".
  const settlementsGrandTotals = useMemo(() => {
    let totalFob = 0;
    let totalKilos = 0;
    settlements.forEach((s) => {
      s.settlement_details?.forEach((d) => {
        totalFob += d.usd_total_fob || 0;
        totalKilos += d.kilos || 0;
      });
    });
    return { totalFob, totalKilos };
  }, [settlements]);

  const rankingsVarietyGrouped = useMemo(() => {
    const grouped: Record<string, ClientRanking> = {};

    // Iterate through all settlements and group by variety
    settlements.forEach((settlement) => {
      settlement.settlement_details?.forEach((detail) => {
        const variety = detail.variety;
        if (!variety) return;

        if (!grouped[variety]) {
          grouped[variety] = {
            client: variety,
            totalKilos: 0,
            totalFob: 0,
            totalKilosWithDFMarket: 0,
            totalFobWithDFMarket: 0,
            weightedDifferenceMarket: { byVolume: 0, byFob: 0 },
            weightedDifferenceClients: { byVolume: 0, byFob: 0 },
            totalSettlements: 0,
            comparableKilosClients: 0,
            comparableFobClients: 0,
            clientComparablePercentage: { byVolume: 0, byFob: 0 },
          };
        }

        // Add detail data
        grouped[variety].totalKilos += detail.kilos;
        grouped[variety].totalFob += detail.usd_total_fob;

        // VTA VS MERCADO
        if (detail.difference_vs_market_eta !== null) {
          grouped[variety].weightedDifferenceMarket.byVolume +=
            detail.difference_vs_market_eta * detail.kilos;
          grouped[variety].weightedDifferenceMarket.byFob +=
            detail.difference_vs_market_eta * detail.usd_total_fob;
          grouped[variety].totalKilosWithDFMarket += detail.kilos;
          grouped[variety].totalFobWithDFMarket += detail.usd_total_fob;
        }
      });
    });

    const result = Object.values(grouped)
      .map((stat) => ({
        ...stat,
        weightedDifferenceMarket: {
          byVolume:
            stat.totalKilosWithDFMarket > 0
              ? stat.weightedDifferenceMarket.byVolume /
              stat.totalKilosWithDFMarket
              : null,
          byFob:
            stat.totalFobWithDFMarket > 0
              ? stat.weightedDifferenceMarket.byFob / stat.totalFobWithDFMarket
              : null,
        },
        weightedDifferenceClients: {
          byVolume: null,
          byFob: null,
        },
        clientComparablePercentage: {
          byVolume: 0,
          byFob: 0,
        },
      }))
      .sort((a, b) => b.totalKilos - a.totalKilos); // Ordenar por volumen (mayor a menor)
    return result;
  }, [settlements]);

  // Calculate rankings grouped by caliber
  const rankingsCaliberGrouped = useMemo(() => {
    const grouped: Record<string, ClientRanking> = {};

    // Obtener los calibers permitidos (si existen)
    const allowedCalibers = speciesCalibers && speciesCalibers.length > 0
      ? new Set(speciesCalibers.map(c => c.toLowerCase()))
      : null;

    // Iterate through all settlements and group by caliber
    settlements.forEach((settlement) => {
      settlement.settlement_details?.forEach((detail) => {
        const caliber = detail.caliber;
        if (!caliber) return;

        // Filtrar por calibres permitidos si existen (case-insensitive)
        if (allowedCalibers && !allowedCalibers.has(caliber.toLowerCase())) return;

        if (!grouped[caliber]) {
          grouped[caliber] = {
            client: caliber,
            totalKilos: 0,
            totalFob: 0,
            totalKilosWithDFMarket: 0,
            totalFobWithDFMarket: 0,
            weightedDifferenceMarket: { byVolume: 0, byFob: 0 },
            weightedDifferenceClients: { byVolume: 0, byFob: 0 },
            totalSettlements: 0,
            comparableKilosClients: 0,
            comparableFobClients: 0,
            clientComparablePercentage: { byVolume: 0, byFob: 0 },
          };
        }

        // Add detail data
        grouped[caliber].totalKilos += detail.kilos;
        grouped[caliber].totalFob += detail.usd_total_fob;

        // VTA VS MERCADO
        if (detail.difference_vs_market_eta !== null) {
          grouped[caliber].weightedDifferenceMarket.byVolume +=
            detail.difference_vs_market_eta * detail.kilos;
          grouped[caliber].weightedDifferenceMarket.byFob +=
            detail.difference_vs_market_eta * detail.usd_total_fob;
          grouped[caliber].totalKilosWithDFMarket += detail.kilos;
          grouped[caliber].totalFobWithDFMarket += detail.usd_total_fob;
        }
      });
    });

    const result = Object.values(grouped)
      .map((stat) => ({
        ...stat,
        weightedDifferenceMarket: {
          byVolume:
            stat.totalKilosWithDFMarket > 0
              ? stat.weightedDifferenceMarket.byVolume /
              stat.totalKilosWithDFMarket
              : null,
          byFob:
            stat.totalFobWithDFMarket > 0
              ? stat.weightedDifferenceMarket.byFob / stat.totalFobWithDFMarket
              : null,
        },
        weightedDifferenceClients: {
          byVolume: null,
          byFob: null,
        },
        clientComparablePercentage: {
          byVolume: 0,
          byFob: 0,
        },
      }))
      .sort((a, b) => getCaliberSortIndex(a.client) - getCaliberSortIndex(b.client));
    return result;
  }, [settlements, speciesCalibers]);

  // Memoize render cell function for exporters data (CIF VS INDUSTRIA y CIF VS EXPORTADORA)
  const renderCellExporters = (
    ranking: ClientRankingsExporters,
    columnKey: string,
    rankings: ClientRanking[],
    grandTotalKilos: number,
    grandTotalFob: number,
    industryLoading: boolean
  ) => {
    // Find the matching client-side ranking. Server-returned `ranking.totalFob`
    // is actually CIF (FOB + freight + revenues) because the edge function
    // overwrites it. For the Volumen/FOB display we want the client-side
    // raw totals, which match what users see elsewhere on the page.
    const matchingRanking = rankings.find((r) => r.client === ranking.client);
    const displayKilos = matchingRanking?.totalKilos ?? ranking.totalKilos;
    const displayFob = matchingRanking?.totalFob ?? 0;

    switch (columnKey) {
      case 'client':
        return (
          <span className='font-medium text-sm'>{ranking.client}</span>
        );
      case 'volume':
        return (
          <VolumeCell
            kilos={displayKilos}
            grandTotalKilos={grandTotalKilos}
          />
        );
      case 'fob':
        return (
          <FobCell fob={displayFob} grandTotalFob={grandTotalFob} />
        );
      case 'difference': // VTA VS MERCADO
        if (!matchingRanking)
          return <span className='text-default-300'>N/A</span>;

        // Coverage always measures kilos; weighting only changes the price delta.
        const marketComparablePercentage =
          matchingRanking.totalKilos > 0
            ? (matchingRanking.totalKilosWithDFMarket / matchingRanking.totalKilos) * 100
            : 0;

        return (
          <DifferenceCell
            value={
              weightingType === 'volume'
                ? matchingRanking.weightedDifferenceMarket.byVolume
                : matchingRanking.weightedDifferenceMarket.byFob
            }
            comparablePercentage={marketComparablePercentage}
            comparableText={t('comparable')}
          />
        );
      case 'difference_exporters': { // CIF VS INDUSTRIA
        const exportersValue =
          weightingType === 'volume'
            ? ranking.weightedDifferenceExporters.byVolume
            : ranking.weightedDifferenceExporters.byFob;
        if (exportersValue === null && industryLoading) {
          return <Spinner size='sm' color='default' />;
        }
        return (
          <DifferenceCell
            value={exportersValue}
            comparablePercentage={
              ranking.exporterComparablePercentage.byVolume
            }
            comparableText={t('comparable')}
          />
        );
      }
      case 'difference_clients': // CIF VS EXPORTADORA
        if (!matchingRanking)
          return <span className='text-default-300'>N/A</span>;

        return (
          <DifferenceCell
            value={
              weightingType === 'volume'
                ? matchingRanking.weightedDifferenceClients.byVolume
                : matchingRanking.weightedDifferenceClients.byFob
            }
            comparablePercentage={
              matchingRanking.clientComparablePercentage.byVolume
            }
            comparableText={t('comparable')}
          />
        );
      default:
        return <span className='text-default-300'>-</span>;
    }
  };

  // Calculate total for VTA VS MERCADO
  const calculateTotalMarket = useCallback(
    (rankingsData: ClientRanking[]) => {
      if (rankingsData.length === 0) return null;

      let totalWeightedDiff = 0;
      let totalWeight = 0;

      rankingsData.forEach((r) => {
        if (!r.weightedDifferenceMarket) return;

        const value =
          weightingType === 'volume'
            ? r.weightedDifferenceMarket.byVolume
            : r.weightedDifferenceMarket.byFob;
        const weight = weightingType === 'volume'
          ? r.totalKilosWithDFMarket  // Changed to only use comparable kilos
          : r.totalFobWithDFMarket;    // Changed to only use comparable fob

        if (value !== null && weight > 0) {
          totalWeightedDiff += value * weight;
          totalWeight += weight;
        }
      });

      if (totalWeight === 0) return null;

      return {
        avg: totalWeightedDiff / totalWeight,
      };
    },
    [weightingType]
  );

  // Calculate total for CIF VS EXPORTADORA
  const calculateTotalClients = useCallback(
    (rankingsData: ClientRanking[]) => {
      if (rankingsData.length === 0) return null;

      let totalWeightedDiff = 0;
      let totalWeight = 0;

      rankingsData.forEach((r) => {
        if (!r.weightedDifferenceClients) return;

        const value =
          weightingType === 'volume'
            ? r.weightedDifferenceClients.byVolume
            : r.weightedDifferenceClients.byFob;
        const weight =
          weightingType === 'volume'
            ? r.comparableKilosClients
            : r.comparableFobClients;

        if (value !== null && weight > 0) {
          totalWeightedDiff += value * weight;
          totalWeight += weight;
        }
      });

      if (totalWeight === 0) return null;

      return {
        avg: totalWeightedDiff / totalWeight,
      };
    },
    [weightingType]
  );

  // Calculate total for CIF VS INDUSTRIA (from backend data)
  const calculateTotalExporters = (rankingsData: ClientRankingsExporters[]) => {
    if (rankingsData.length === 0) return null;

    let totalWeightedDiff = 0;
    let totalWeight = 0;

    rankingsData.forEach((r) => {
      if (!r.weightedDifferenceExporters) return;

      const value =
        weightingType === 'volume'
          ? r.weightedDifferenceExporters.byVolume
          : r.weightedDifferenceExporters.byFob;
      const weight =
        weightingType === 'volume'
          ? r.comparableKilosExporters
          : r.comparableFobExporters;

      if (value !== null && weight > 0) {
        totalWeightedDiff += value * weight;
        totalWeight += weight;
      }
    });

    if (totalWeight === 0) return null;

    return {
      avg: totalWeightedDiff / totalWeight,
    };
  };

  const handleExportCurrentTab = useCallback(() => {
    const tabConfig: Record<string, { data: ClientRankingsExporters[]; grouped: ClientRanking[]; label: string; columns: readonly { name: string; uid: string }[] }> = {
      client: { data: rankingsClient, grouped: rankings, label: 'cliente', columns: COLUMNS_CLIENT },
      variety: { data: rankingsVariety, grouped: rankingsVarietyGrouped, label: 'variedad', columns: COLUMNS_VARIETY_CALIBER },
      caliber: { data: rankingsCaliber, grouped: rankingsCaliberGrouped, label: 'calibre', columns: COLUMNS_VARIETY_CALIBER },
    };
    const config = tabConfig[activeTab];
    if (!config) return;

    const exportRows = config.data.map((ranking) => {
      const matchingRanking = config.grouped.find((r) => r.client === ranking.client);
      return config.columns.map((col) => {
        switch (col.uid) {
          case 'client': return ranking.client;
          case 'difference': {
            if (!matchingRanking) return 'N/A';
            const val = weightingType === 'volume' ? matchingRanking.weightedDifferenceMarket.byVolume : matchingRanking.weightedDifferenceMarket.byFob;
            return val !== null ? parseFloat(val.toFixed(1)) : 'N/A';
          }
          case 'difference_exporters': {
            const val = weightingType === 'volume' ? ranking.weightedDifferenceExporters.byVolume : ranking.weightedDifferenceExporters.byFob;
            return val !== null ? parseFloat(val.toFixed(1)) : 'N/A';
          }
          case 'difference_clients': {
            if (!matchingRanking) return 'N/A';
            const val = weightingType === 'volume' ? matchingRanking.weightedDifferenceClients.byVolume : matchingRanking.weightedDifferenceClients.byFob;
            return val !== null ? parseFloat(val.toFixed(1)) : 'N/A';
          }
          default: return '';
        }
      });
    });
    exportTableToExcel({
      fileName: `ranking-${config.label}`,
      sheetName: config.label,
      headers: config.columns.map((col) => col.name),
      rows: exportRows,
    });
  }, [activeTab, rankingsClient, rankings, rankingsVariety, rankingsVarietyGrouped, rankingsCaliber, rankingsCaliberGrouped, weightingType, COLUMNS_CLIENT, COLUMNS_VARIETY_CALIBER]);

  // Memoize handler
  const handleWeightingChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setWeightingType(e.target.value as 'volume' | 'fob');
    },
    []
  );

  const getSortValue = (
    ranking: ClientRankingsExporters,
    columnKey: string,
    groupedRankings: ClientRanking[]
  ): number | string => {
    const matchingRanking = groupedRankings.find((r) => r.client === ranking.client);
    switch (columnKey) {
      case 'client':
        return ranking.client;
      case 'volume':
        return matchingRanking?.totalKilos ?? ranking.totalKilos;
      case 'fob':
        return matchingRanking?.totalFob ?? 0;
      case 'difference':
        if (!matchingRanking) return -Infinity;
        return (weightingType === 'volume'
          ? matchingRanking.weightedDifferenceMarket.byVolume
          : matchingRanking.weightedDifferenceMarket.byFob) ?? -Infinity;
      case 'difference_exporters':
        return (weightingType === 'volume'
          ? ranking.weightedDifferenceExporters.byVolume
          : ranking.weightedDifferenceExporters.byFob) ?? -Infinity;
      case 'difference_clients':
        if (!matchingRanking) return -Infinity;
        return (weightingType === 'volume'
          ? matchingRanking.weightedDifferenceClients.byVolume
          : matchingRanking.weightedDifferenceClients.byFob) ?? -Infinity;
      default:
        return 0;
    }
  };

  // Function to render tab content with table and totals
  const renderTabContent = (
    rankingsData: ClientRankingsExporters[],
    groupedRankings: ClientRanking[],
    label: string,
    columns: readonly { name: string; uid: string }[]
  ) => {
    const totalMarket = calculateTotalMarket(groupedRankings);
    const totalClients = calculateTotalClients(groupedRankings);
    const totalExporters = calculateTotalExporters(rankingsData);

    // Row set is driven by the CLIENT-SIDE groupedRankings — which respects
    // sidebar filters that the server doesn't know about (notably ship/week
    // narrowing). We merge in server-side values per row for the CIF VS
    // INDUSTRIA column. Any row without a server match shows a spinner (if
    // the server response is still in flight) or N/A (if it has landed but
    // this client wasn't in the industry response).
    const serverByClient = new Map<string, ClientRankingsExporters>();
    rankingsData.forEach((r) => {
      serverByClient.set(r.client, r);
    });
    const serverDataReady = rankingsData.length > 0;
    const effectiveData: ClientRankingsExporters[] = groupedRankings.map(
      (g) => {
        const s = serverByClient.get(g.client);
        return {
          client: g.client,
          totalKilos: g.totalKilos,
          totalFob: g.totalFob,
          weightedDifferenceExporters: s?.weightedDifferenceExporters ?? {
            byVolume: null,
            byFob: null,
          },
          exporterComparablePercentage: s?.exporterComparablePercentage ?? {
            byVolume: 0,
            byFob: 0,
          },
          totalSettlements: s?.totalSettlements ?? 0,
          comparableKilosExporters: s?.comparableKilosExporters ?? 0,
          comparableFobExporters: s?.comparableFobExporters ?? 0,
        } as unknown as ClientRankingsExporters;
      }
    );
    const industryLoading = !serverDataReady && loading;

    // Sort data. When no user-selected sort is active, default to totalFob
    // desc (biggest first) — including during the pre-server-response window
    // where rows come from client-side groupedRankings stubs.
    const sortedData = [...effectiveData].sort((a, b) => {
      if (!sortDescriptor.column) {
        const aFob =
          groupedRankings.find((g) => g.client === a.client)?.totalFob ??
          a.totalFob ?? 0;
        const bFob =
          groupedRankings.find((g) => g.client === b.client)?.totalFob ??
          b.totalFob ?? 0;
        return bFob - aFob;
      }
      const aVal = getSortValue(a, sortDescriptor.column as string, groupedRankings);
      const bVal = getSortValue(b, sortDescriptor.column as string, groupedRankings);
      let cmp: number;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        cmp = aVal.localeCompare(bVal);
      } else {
        cmp = (aVal as number) - (bVal as number);
      }
      return sortDescriptor.direction === 'descending' ? -cmp : cmp;
    });

    // Grand totals are derived from the raw filtered settlements (see
    // settlementsGrandTotals memo) so they stay consistent across tabs —
    // regardless of whether a tab's grouping happens to exclude some details
    // (e.g. caliber not in species list).
    const grandTotalKilos = settlementsGrandTotals.totalKilos;
    const grandTotalFob = settlementsGrandTotals.totalFob;

    // Paginate visible rows. Totals row is always visible and doesn't count
    // toward the 10-row limit.
    const visibleRows = sortedData.slice(0, visibleCount);
    const hasMore = sortedData.length > visibleCount;

    // Total row FIRST — drives the eye to summary metrics before rows.
    const dataWithTotal =
      visibleRows.length > 0
        ? [
            {
              client: 'TOTAL',
              totalKilos: grandTotalKilos,
              totalFob: grandTotalFob,
              weightedDifferenceExporters: { byVolume: null, byFob: null },
              exporterComparablePercentage: { byVolume: 0, byFob: 0 },
              totalSettlements: 0,
              comparableKilosExporters: 0,
              comparableFobExporters: 0,
              isTotal: true,
            } as ClientRankingsExporters & { isTotal: boolean },
            ...visibleRows,
          ]
        : [];

    return (
      <>
      <Table
        aria-label={t('Ranking por {label}', { label })}
        key={`${label}-${weightingType}`}
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}
        isHeaderSticky
        classNames={{
          ...UNIFIED_TABLE_CLASSNAMES,
          base: 'max-h-[600px] overflow-auto',
        }}
      >
        <TableHeader>
          {columns.map((column) => (
            <TableColumn key={column.uid} allowsSorting>
              {column.uid === 'difference_clients' && hasActiveFilters ? (
                <div className='flex items-center gap-1.5'>
                  <span>{column.name}</span>
                  <Chip
                    size='sm'
                    color='warning'
                    variant='flat'
                    className='h-4 text-[9px] px-1.5 min-h-0'
                    title={t(
                      'Esta comparación se calcula contra los otros clientes; los filtros activos reducen el conjunto de comparación.'
                    )}
                  >
                    {t('filtrado')}
                  </Chip>
                </div>
              ) : (
                column.name
              )}
            </TableColumn>
          ))}
        </TableHeader>
        <TableBody
          items={dataWithTotal}
          emptyContent={t('No hay datos para mostrar')}
        >
          {(ranking: ClientRankingsExporters & { isTotal?: boolean }) => (
            <TableRow
              key={ranking.client}
              className={
                ranking.isTotal
                  ? `${SUMMARY_ROW_CLASS} border-b-2 border-default-200 dark:border-default-100`
                  : ''
              }
            >
              {columns.map((column) => (
                <TableCell key={column.uid}>
                  {ranking.isTotal ? (
                    // Total row cells
                    column.uid === 'client' ? (
                      <span className='font-bold text-sm'>{t('TOTAL')}</span>
                    ) : column.uid === 'volume' ? (
                      <span className='font-bold tabular-nums text-sm flex justify-end'>
                        {formatNumber(grandTotalKilos, { decimals: 0 })}
                      </span>
                    ) : column.uid === 'fob' ? (
                      <span className='font-bold tabular-nums text-sm flex justify-end'>
                        {formatMoneyUSD(grandTotalFob, { decimals: 0 })}
                      </span>
                    ) : column.uid === 'difference' ? (
                      totalMarket ? (
                        <DifferenceCell value={totalMarket.avg} />
                      ) : (
                        <span>N/A</span>
                      )
                    ) : column.uid === 'difference_exporters' ? (
                      industryLoading ? (
                        <Spinner size='sm' color='default' />
                      ) : totalExporters ? (
                        <DifferenceCell value={totalExporters.avg} />
                      ) : (
                        <span>N/A</span>
                      )
                    ) : column.uid === 'difference_clients' ? (
                      totalClients ? (
                        <DifferenceCell value={totalClients.avg} />
                      ) : (
                        <span>N/A</span>
                      )
                    ) : null
                  ) : (
                    // Normal data row
                    renderCellExporters(
                      ranking,
                      column.uid,
                      groupedRankings,
                      grandTotalKilos,
                      grandTotalFob,
                      industryLoading
                    )
                  )}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
      {hasMore && (
        <div className='flex justify-center mt-2'>
          <Button
            variant='light'
            size='sm'
            onPress={() =>
              setVisibleCount((c) => Math.min(c + 10, sortedData.length))
            }
            className='w-full'
          >
            {t('Cargar más')} ({sortedData.length - visibleCount}{' '}
            {t('restantes')})
          </Button>
        </div>
      )}
      </>
    );
  };

  const isRefreshing = loading && hasLoadedOnce;

  // Show the full-card spinner only when we have nothing to paint yet —
  // neither client-side rankings nor server-side ones. As soon as client-side
  // data is available, render the table immediately and show a small spinner
  // in the CIF VS INDUSTRIA column while the server call finishes.
  const hasAnyClientData =
    rankings.length > 0 ||
    rankingsVarietyGrouped.length > 0 ||
    rankingsCaliberGrouped.length > 0;
  const hasAnyServerData =
    rankingsClient.length > 0 ||
    rankingsVariety.length > 0 ||
    rankingsCaliber.length > 0;
  const showFullSpinner =
    loading && !hasLoadedOnce && !hasAnyClientData && !hasAnyServerData;

  return (
    <>
      {showFullSpinner ? (
        <Card className='w-full p-6'>
          <div className='flex justify-center items-center h-64'>
            <div className='flex flex-col items-center gap-4'>
              <Spinner size='lg' color='primary' />
              <p className='text-default-400 font-medium'>
                {t('Cargando Desempeño Clientes...')}
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className={isRefreshing ? 'opacity-70 pointer-events-none transition-opacity' : 'transition-opacity'}>
          <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3'>
            <div className='flex flex-col gap-2'>
              <div className='flex items-center gap-2'>
                <h3 className='text-xl font-semibold'>{t('Desempeño Comercial')}</h3>
                {isRefreshing && (
                  <span className='text-xs text-default-400 font-normal'>
                    {t('Actualizando...')}
                  </span>
                )}
                <InformationTooltip>
                  <div className='space-y-4'>
                    <div>
                      <h3 className='font-semibold mb-1'>
                        {t('Cálculo de Rankings')}
                      </h3>
                      <p className='text-sm text-default-500 mb-3'>
                        {t('Los rankings se calculan comparando precios CIF/kg (Precio Venta - Costos en Destino sin considerar Flete) para productos idénticos (misma variedad-calibre-color-formato). Las diferencias pueden ser ponderadas por:')}
                      </p>
                      <ul className='list-disc pl-4 text-sm space-y-2'>
                        <li>
                          <span className='font-medium'>{t('Por Volumen:')}</span> {t('Prioriza diferencias en productos con mayor volumen de kilos')}
                        </li>
                        <li>
                          <span className='font-medium'>{t('Por Venta:')}</span> {t('Prioriza diferencias en productos con mayor valor de venta total')}
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h3 className='font-semibold mb-1'>
                        {t('Tipos de Comparación')}
                      </h3>
                      <ul className='list-disc pl-4 text-sm space-y-2'>
                        <li>
                          <span className='font-medium'>{t('VTA VS MERCADO:')}</span>{' '}
                          {t('Compara los precios de venta contra la mediana del precio de mercado para cada producto registrado al momento de llegar la fruta al mercado (ventana de 3 días a partir de la fecha ETA).')}
                        </li>
                        <li>
                          <span className='font-medium'>{t('CIF VS INDUSTRIA:')}</span>{' '}
                          {t('Para cada producto, compara el retorno CIF contra la mediana de los retornos CIF de otras exportadoras que ocupan la plataforma. Para la comparación, se toman mismas naves en el caso de los marítimos y mismas semanas en el caso de los aéreos. El porcentaje comparable indica qué proporción de los kilos totales tiene datos para comparar, independientemente de la ponderación seleccionada.')}
                        </li>
                        <li>
                          <span className='font-medium'>{t('CIF VS EXPORTADORA:')}</span>{' '}
                          {t('Para cada producto, compara el retorno CIF contra la mediana de los retornos CIF de los otros clientes. Para la comparación, se toman mismas naves en el caso de los marítimos y mismas semanas en el caso de los aéreos. El porcentaje comparable indica qué proporción de los kilos totales tiene datos para comparar, independientemente de la ponderación seleccionada.')}
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h3 className='font-semibold mb-1'>
                        {t('Agrupaciones y Totales')}
                      </h3>
                      <p className='text-sm text-default-500'>
                        {t('Los datos pueden visualizarse agrupados por Cliente, Variedad o Calibre. El total mostrado en cada vista es específico para ese agrupamiento, calculando el promedio ponderado de todas las filas en esa agrupación.')}
                      </p>
                      <p className='text-sm text-default-500 mt-2'>
                        {t('Los valores positivos (verde) indican precios por encima de la mediana, mientras que los negativos (rojo) indican precios por debajo.')}
                      </p>
                    </div>
                  </div>
                </InformationTooltip>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <Select
                label={t('Ponderación')}
                value={weightingType}
                onChange={handleWeightingChange}
                size='sm'
                defaultSelectedKeys={['fob']}
                className='w-full sm:w-[280px]'
              >
                <SelectItem key='fob' value='fob' description={t('Pondera por valor de venta total')}>
                  {t('Por Venta')}
                </SelectItem>
                <SelectItem key='volume' value='volume' description={t('Pondera por volumen de kilos')}>
                  {t('Por Volumen')}
                </SelectItem>
              </Select>
              <ExcelExportButton onClick={handleExportCurrentTab} />
            </div>
          </div>
          <div className='overflow-x-auto'>
            <div className='min-w-[768px]'>
              <Tabs
                aria-label={t('Opciones de agrupación')}
                selectedKey={activeTab}
                onSelectionChange={(key) => setActiveTab(key as string)}
                className='mb-4'
                size='lg'
                variant='underlined'
              >
                <Tab key='client' title={t('Por Cliente')} />
                <Tab key='variety' title={t('Por Variedad')} />
                <Tab key='caliber' title={t('Por Calibre')} />
              </Tabs>
            </div>
          </div>
          <Card className='w-full'>
            <CardBody className='p-0'>
              {activeTab === 'client' &&
                renderTabContent(
                  rankingsClient,
                  rankings,
                  'cliente',
                  COLUMNS_CLIENT
                )}
              {activeTab === 'variety' &&
                renderTabContent(
                  rankingsVariety,
                  rankingsVarietyGrouped,
                  'variedad',
                  COLUMNS_VARIETY_CALIBER
                )}
              {activeTab === 'caliber' &&
                renderTabContent(
                  rankingsCaliber,
                  rankingsCaliberGrouped,
                  'calibre',
                  COLUMNS_VARIETY_CALIBER
                )}
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
};

export default memo(RankingClientsTable);
